import { type JSX } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Alert,
  QRCodeDisplay,
} from '../../components';
import {
  generateEncryptionKey,
  encrypt,
  exportKeyToBytes,
  uint8ArrayToBase64Url,
} from '../../crypto';
import { createSecret } from '../../api';
import { useAuth } from '../../contexts/AuthContext';

export function CreateSecretPage(): JSX.Element {
  const { user } = useAuth();
  const [plaintext, setPlaintext] = useState('');
  const [hint, setHint] = useState('');
  const [ttl, setTtl] = useState('3600'); // 1 hour default
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ secretId: string; url: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [enableNotifications, setEnableNotifications] = useState(false);

  // Reset notification checkbox when user authentication changes
  useEffect(() => {
    setEnableNotifications(user?.authenticated ?? false);
  }, [user?.authenticated]);

  const handleSubmit = async (e: JSX.TargetedEvent<HTMLFormElement, Event>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Validation
      if (!plaintext.trim()) {
        throw new Error('Please enter a secret');
      }

      if (ttl && (isNaN(Number(ttl)) || Number(ttl) < 1)) {
        throw new Error('TTL must be a positive number');
      }

      // Generate encryption key
      const key = await generateEncryptionKey();

      // Encrypt the secret
      const encryptedData = await encrypt(plaintext, key);

      // Validate encrypted secret size (8KB limit)
      const secretSize = encryptedData.c.length + encryptedData.iv.length;
      if (secretSize > 8192) {
        throw new Error(`Secret is too large (${Math.round(secretSize / 1024)}KB). Maximum size is 8KB. Try shortening your secret.`);
      }

      // Encrypt the hint with the same key (if provided)
      const encryptedHintData = hint.trim() ? await encrypt(hint, key) : null;

      // Validate encrypted hint size (1KB limit)
      if (encryptedHintData) {
        const hintSize = encryptedHintData.c.length + encryptedHintData.iv.length;
        if (hintSize > 1024) {
          throw new Error(`Hint is too large (${Math.round(hintSize / 1024)}KB). Maximum size is 1KB.`);
        }
      }

      // Export key for URL fragment (using URL-safe base64)
      const keyBytes = await exportKeyToBytes(key);
      const keyBase64 = uint8ArrayToBase64Url(keyBytes);

      // Create secret on server (both secret and hint are EncryptedData objects)
      // Send notification flag only if user is authenticated and checkbox is ticked
      const result = await createSecret(
        encryptedData,
        encryptedHintData,
        Number(ttl),
        enableNotifications && user?.authenticated
      );

      // Construct full URL with key in fragment (never sent to server)
      const baseUrl = window.location.origin;
      const secretUrl = `${baseUrl}/secret/${result.secretId}#${keyBase64}`;

      setSuccess({
        secretId: result.secretId,
        url: secretUrl,
      });

      // Clear form and reset copy state
      setPlaintext('');
      setHint('');
      setTtl('3600');
      setCopied(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Create a One-Time Secret</CardTitle>
          <CardDescription>
            Encrypt and share a secret that self-destructs after a single retrieval.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert type="warning" title="Security Notice" className="mb-6">
            <ul className="list-disc list-inside space-y-1">
              <li>For maximum security, use a personal device on a personal network</li>
            </ul>
          </Alert>

          {error && (
            <Alert type="error" title="Error" className="mb-6">
              {error}
            </Alert>
          )}

          {success && (
            <Alert type="success" title="Secret Created!" className="mb-6">
              <div className="space-y-4">
                <p>Your secret has been encrypted and stored. Share this link:</p>

                {/* Link and QR Code side-by-side on desktop (50:50), stacked on mobile */}
                <div className="flex flex-col md:flex-row md:items-start md:gap-6">
                  {/* Secret URL with buttons - 50% width on desktop */}
                  <div className="w-full md:w-1/2 mb-4 md:mb-0 md:mt-8">
                    <div className="bg-white dark:bg-gray-700 rounded p-3 border border-green-300 dark:border-green-600 break-all font-mono text-sm max-h-32 overflow-y-auto mb-3">
                      <a
                        href={success.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
                      >
                        {success.url}
                      </a>
                    </div>

                    {/* Action buttons under the link */}
                    <div className="flex flex-wrap gap-2 justify-center">
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(success.url);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        variant="primary"
                        size="md"
                      >
                        {copied ? '✓ Copied!' : 'Copy to clipboard'}
                      </Button>
                      <Button
                        onClick={() => setSuccess(null)}
                        variant="secondary"
                        size="md"
                      >
                        Create another secret
                      </Button>
                    </div>
                  </div>

                  {/* QR Code - 50% width on desktop */}
                  <div className="w-full md:w-1/2 flex justify-center md:justify-start">
                    <QRCodeDisplay
                      value={success.url}
                      size={280}
                      title="Scan to open"
                    />
                  </div>
                </div>
              </div>
            </Alert>
          )}

          {!success && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Secret (required)
                </label>
                <textarea
                  value={plaintext}
                  onChange={(e) => setPlaintext(e.target.value)}
                  placeholder="Enter the secret you want to share"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-800"
                  rows={3}
                  disabled={isLoading}
                />
                <div className="text-sm font-small text-gray-500 dark:text-gray-400">🔐 Encrypted in your browser, we never receive the decryption key.</div>
              </div>

              <div>
                <Input
                  label="Hint for recipient (optional)."
                  value={hint}
                  onChange={(e) => setHint(e.target.value)}
                  placeholder=""
                  disabled={isLoading}
                />
                <div className="text-sm font-small text-gray-500 dark:text-gray-400">🔐 Encrypted with the secret, only visible to someone with the full link.</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Time To Live (TTL)
                </label>
                <select
                  value={ttl}
                  onChange={(e) => setTtl(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-800"
                >
                  <option value="3600">1 Hour</option>
                  <option value="86400">24 Hours</option>
                  <option value="604800" disabled={!user?.authenticated}>
                    7 Days {!user?.authenticated ? '(sign-in required)' : ''}
                  </option>
                  <option value="2592000" disabled={!user?.authenticated}>
                    30 Days {!user?.authenticated ? '(sign-in required)' : ''}
                  </option>
                </select>
                <div className="text-sm font-small text-gray-500 dark:text-gray-400">After expiration, the encrypted data is permanently deleted and cannot be recovered.</div>
              </div>

              <div className="space-y-2 pt-2">
                <label className={`flex items-center gap-2 ${user?.authenticated ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                  <input
                    type="checkbox"
                    checked={enableNotifications}
                    onChange={(e) => setEnableNotifications(e.target.checked)}
                    disabled={isLoading || !user?.authenticated}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {user?.authenticated && user.email ? (
                      <>Email me on <span className="font-semibold">{user.email}</span> when the secret has been retrieved or expired</>
                    ) : (
                      <>Email me when the secret is retrieved or expires (requires sign-in)</>
                    )}
                  </span>
                </label>
              </div>

              <div className="text-sm text-gray-600 dark:text-gray-400 mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
                {user?.authenticated ? (
                  <p>✓ You can create up to <strong>100 secrets per day</strong> while signed in.</p>
                ) : (
                  <p>
                    You can create up to <strong>10 secrets per day</strong>. Sign in to increase this to <strong>100 per day</strong>.
                  </p>
                )}
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button type="submit" disabled={isLoading} size="lg">
                  {isLoading ? 'Creating...' : 'Create Secret'}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
